import React, { useState, useEffect } from 'react';
import { X, MapPin, Clock, ChevronDown, Sparkles } from 'lucide-react';
import MapboxLocationSearch from './MapboxLocationSearch';

const EventCreationDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  selectedDay = 1, 
  dayDate = null,
  initialData = null 
}) => {
  // Animation and touch handling state
  const [isAnimating, setIsAnimating] = useState(false);
  const [dialogStyle, setDialogStyle] = useState({ transform: 'translateY(100%)' });
  const [touchStart, setTouchStart] = useState(null);
  const [touchCurrent, setTouchCurrent] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Form data state
  const [eventFormData, setEventFormData] = useState({
    title: '',
    start: '',
    end: '',
    location: '',
    description: '',
    category: '',
  });

  // Categories list
  const categories = [
    'Cultural & Historical',
    'Arts & Entertainment',
    'Culinary & Gastronomy',
    'Outdoor & Adventure',
    'Nature & Wildlife',
    'Relaxation & Wellness',
    'Sports & Fitness',
    'Shopping & Markets',
    'Nightlife & Social',
    'Events & Festivals',
    'Guided Tours & Excursions',
    'Educational & Workshops',
    'Family & Kids',
    'Photography & Scenic',
    'Spiritual & Religious',
    'Volunteer & Community',
    'Unique & Offbeat',
    'Transportation',
    'Others'
  ];

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open) {
      if (initialData) {
        setEventFormData(initialData);
      } else {
        setEventFormData({
          title: '',
          start: '',
          end: '',
          location: '',
          description: '',
          category: '',
        });
      }
      animateDialogOpen();
    }
  }, [open, initialData]);

  // Animation functions
  const animateDialogOpen = () => {
    setIsAnimating(true);
    setDialogStyle({ transform: 'translateY(100%)' });
    
    requestAnimationFrame(() => {
      setTimeout(() => {
        setDialogStyle({ 
          transform: 'translateY(0%)', 
          transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
        });
      }, 10);
    });
  };

  const animateDialogClose = () => {
    setDialogStyle({ 
      transform: 'translateY(100%)', 
      transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
    });
    
    setTimeout(() => {
      setIsAnimating(false);
      setDropdownOpen(false);
      setEventFormData({
        title: '',
        start: '',
        end: '',
        location: '',
        description: '',
        category: '',
      });
      onClose();
    }, 300);
  };

  // Touch handling functions
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientY);
    setTouchCurrent(e.touches[0].clientY);
    setIsDragging(false);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStart;
    setTouchCurrent(currentY);
    
    // Only allow dragging down
    if (diff > 0) {
      setIsDragging(true);
      setDialogStyle({ 
        transform: `translateY(${diff}px)`,
        transition: 'none'
      });
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchStart || !touchCurrent) return;
    
    const diff = touchCurrent - touchStart;
    const velocity = diff / 10;
    
    // Close if dragged down more than 100px or with sufficient velocity
    if (diff > 100 || velocity > 5) {
      animateDialogClose();
    } else {
      // Snap back to original position
      setDialogStyle({ 
        transform: 'translateY(0%)', 
        transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
      });
    }
    
    setTouchStart(null);
    setTouchCurrent(null);
    setIsDragging(false);
  };

  // Form handlers
  const handleEventInputChange = (e) => {
    const { name, value } = e.target;
    setEventFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (location) => {
    setEventFormData(prev => ({ ...prev, location }));
  };

  const handleCategorySelect = (category) => {
    setEventFormData(prev => ({ ...prev, category }));
    setDropdownOpen(false);
  };

  const handleEventSubmit = (e) => {
    e.preventDefault();
    
    const eventData = {
      ...eventFormData,
      day: selectedDay,
      id: Date.now() // Simple ID for frontend state
    };

    onSubmit(eventData);
    animateDialogClose();
  };

  const handleCloseDialog = () => {
    animateDialogClose();
  };

  if (!open && !isAnimating) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        background: 'linear-gradient(135deg, rgba(255, 182, 138, 0.95) 0%, rgba(255, 138, 101, 0.95) 50%, rgba(255, 160, 133, 0.95) 100%)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleCloseDialog();
        }
      }}
    >
      <div 
        style={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%',
          maxWidth: '768px',
          margin: '0 auto',
          ...dialogStyle
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >

        {/* Main Card */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 245, 235, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px 24px 0 0',
          boxShadow: '0 -10px 25px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          borderBottom: 'none',
          minHeight: '60vh',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}>
          {/* Decorative Elements */}
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '128px',
            height: '128px',
            background: 'linear-gradient(135deg, rgba(255, 200, 150, 0.2) 0%, rgba(255, 182, 193, 0.2) 100%)',
            borderRadius: '50%',
            filter: 'blur(48px)'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '160px',
            height: '160px',
            background: 'linear-gradient(45deg, rgba(255, 218, 185, 0.2) 0%, rgba(255, 127, 80, 0.2) 100%)',
            borderRadius: '50%',
            filter: 'blur(48px)'
          }}></div>

          {/* Header */}
          <div style={{
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.9) 0%, rgba(255, 127, 80, 0.9) 50%, rgba(255, 192, 203, 0.9) 100%)',
            padding: '24px'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)'
            }}></div>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: 'white',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Sparkles size={24} />
                  {selectedDay ? `Add Event to Day ${selectedDay}` : 'Add New Event'}
                </h2>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px',
                  margin: '4px 0 0 0'
                }}>
                  {dayDate || 'Create your perfect travel experience'}
                </p>
              </div>
              <button
                onClick={handleCloseDialog}
                style={{
                  padding: '8px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  cursor: 'pointer',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                <X size={20} color="white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleEventSubmit}>
            <div style={{ position: 'relative', padding: '32px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Activity Title */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Activity Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={eventFormData.title}
                  onChange={handleEventInputChange}
                  placeholder="Give your activity a memorable name"
                  autoFocus
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid #FED7AA',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#FB923C';
                    e.target.style.background = 'white';
                    e.target.style.boxShadow = '0 0 0 4px rgba(251, 146, 60, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#FED7AA';
                    e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Time Fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Clock size={16} color="#FB923C" />
                    Start Time
                  </label>
                  <input
                    type="datetime-local"
                    name="start"
                    value={eventFormData.start}
                    onChange={handleEventInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '16px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid #FED7AA',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FB923C';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 4px rgba(251, 146, 60, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#FED7AA';
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Clock size={16} color="#FB923C" />
                    End Time
                  </label>
                  <input
                    type="datetime-local"
                    name="end"
                    value={eventFormData.end}
                    onChange={handleEventInputChange}
                    min={eventFormData.start}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '16px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid #FED7AA',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FB923C';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 4px rgba(251, 146, 60, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#FED7AA';
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Location */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <MapPin size={16} color="#FB923C" />
                  Location
                </label>
                <MapboxLocationSearch
                  value={eventFormData.location}
                  onChange={handleLocationChange}
                  placeholder="Search for a specific place or landmark..."
                  style={{ width: '100%' }}
                />
              </div>

              {/* Category Dropdown */}
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Category
                </label>
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid #FED7AA',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => e.target.style.borderColor = '#FB923C'}
                  onMouseLeave={(e) => e.target.style.borderColor = '#FED7AA'}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#FB923C';
                    e.target.style.background = 'white';
                    e.target.style.boxShadow = '0 0 0 4px rgba(251, 146, 60, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#FED7AA';
                    e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <span style={{ color: eventFormData.category ? '#111827' : '#9CA3AF' }}>
                    {eventFormData.category || 'Choose an activity type'}
                  </span>
                  <ChevronDown 
                    size={20} 
                    color="#FB923C" 
                    style={{ 
                      transition: 'transform 0.2s',
                      transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }} 
                  />
                </button>

                {dropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '8px',
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
                    border: '1px solid #FED7AA',
                    maxHeight: '256px',
                    overflowY: 'auto',
                    zIndex: 10
                  }}>
                    <div style={{ padding: '8px 0' }}>
                      {categories.map((category) => (
                        <button
                          key={category}
                          type="button"
                          onClick={() => handleCategorySelect(category)}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            color: '#374151',
                            fontSize: '14px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)';
                            e.target.style.color = '#FB923C';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'none';
                            e.target.style.color = '#374151';
                          }}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Description
                </label>
                <textarea
                  name="description"
                  value={eventFormData.description}
                  onChange={handleEventInputChange}
                  placeholder="Share the details that will make this activity special..."
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid #FED7AA',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    resize: 'none',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#FB923C';
                    e.target.style.background = 'white';
                    e.target.style.boxShadow = '0 0 0 4px rgba(251, 146, 60, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#FED7AA';
                    e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', paddingTop: '16px' }}>
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    borderRadius: '16px',
                    background: '#F3F4F6',
                    border: 'none',
                    color: '#374151',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontSize: '16px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#E5E7EB'}
                  onMouseLeave={(e) => e.target.style.background = '#F3F4F6'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!eventFormData.title}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    borderRadius: '16px',
                    background: eventFormData.title 
                      ? 'linear-gradient(135deg, #FB923C 0%, #FF7F50 100%)' 
                      : '#D1D5DB',
                    border: 'none',
                    color: 'white',
                    fontWeight: '600',
                    cursor: eventFormData.title ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                    fontSize: '16px',
                    transform: 'translateY(0)',
                    boxShadow: eventFormData.title ? '0 4px 12px rgba(251, 146, 60, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (eventFormData.title) {
                      e.target.style.background = 'linear-gradient(135deg, #F97316 0%, #FF6347 100%)';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 25px rgba(251, 146, 60, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (eventFormData.title) {
                      e.target.style.background = 'linear-gradient(135deg, #FB923C 0%, #FF7F50 100%)';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(251, 146, 60, 0.3)';
                    }
                  }}
                >
                  Create Activity
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventCreationDialog;